import { match } from "ts-pattern";

import type {
  OrganisationId,
  OrganisationStructure,
} from "@thalamos/haven-common";

export type StructureOrganisation =
  OrganisationStructure["organisations"][number];

/** How a card stands to the organisation the structure is drawn around. */
export type Standing =
  | { kind: "focus" }
  | { kind: "above"; levels: number }
  | { kind: "below"; levels: number }
  | { kind: "operates" }
  | { kind: "operated-by" };

export type PlacedCard =
  | {
      kind: "organisation";
      key: string;
      organisation: StructureOrganisation;
      standing: Standing;
      x: number;
      y: number;
    }
  | {
      kind: "more";
      key: string;
      /** The group the card stands in for: whose members, or what the focus operates. */
      groupOf: string;
      count: number;
      x: number;
      y: number;
    };

export type PlacedEdge = {
  key: string;
  kind: "member_of" | "operated_by";
  /** For a membership, the organisation receiving what travels up it. */
  receiverId: OrganisationId;
  points: readonly (readonly [number, number])[];
};

export type StructureLayout = {
  /** In reading order, top to bottom then left to right, so keyboard focus moves through them as they are drawn. */
  cards: readonly PlacedCard[];
  edges: readonly PlacedEdge[];
  /** The centre of the focus's card. */
  focus: { x: number; y: number };
  /** Where the one label saying what operating means goes, if anything operates. */
  operatingLabel: { x: number; y: number; width: number } | undefined;
  width: number;
  height: number;
};

export const CARD_WIDTH = 216;
/** Room for a name, its type, its standing and a setting that wraps onto a second line. */
export const CARD_HEIGHT = 128;
const GAP_X = 24;
const ROW_GAP = 96;
const ROW_HEIGHT = CARD_HEIGHT + ROW_GAP;
const MARGIN = 32;
/** Between the focus and what it operates or is operated by: room for the label saying what that means. */
const SIDE_GAP = 176;
/** How far above the focus's row a line to a farther operated card runs, clear of the nearer ones. */
const BUS_RISE = 16;
/** Between neighbouring lanes, where several memberships skip past rows of cards. */
const LANE_GAP = 8;

/** Past this many, a group other than the focus's own members shows its first few and a count. */
const GROUP_LIMIT = 6;
const GROUP_SHOWN = 5;

/** The `groupOf` for what the focus operates; member groups use their parent's id, which this can never equal. */
export const operatedGroup = "operated";

type Slot =
  | { kind: "organisation"; id: OrganisationId }
  | { kind: "more"; groupOf: string; count: number };

const byName =
  (organisationOf: ReadonlyMap<OrganisationId, StructureOrganisation>) =>
  (a: OrganisationId, b: OrganisationId) =>
    (organisationOf.get(a)?.name ?? "").localeCompare(
      organisationOf.get(b)?.name ?? "",
    ) || a.localeCompare(b);

/** A group's slots: in full, or those always shown, the first few others and how many more. */
const grouped = ({
  ids,
  groupOf,
  inFull,
  alwaysShown,
}: {
  ids: readonly OrganisationId[];
  groupOf: string;
  inFull: boolean;
  alwaysShown: ReadonlySet<OrganisationId>;
}): Slot[] => {
  const hideable = ids.filter((id) => !alwaysShown.has(id));
  if (inFull || hideable.length <= GROUP_LIMIT) {
    return ids.map((id) => ({ kind: "organisation", id }));
  }
  const shown = new Set(hideable.slice(0, GROUP_SHOWN));
  return [
    ...ids
      .filter((id) => alwaysShown.has(id) || shown.has(id))
      .map((id): Slot => ({ kind: "organisation", id })),
    { kind: "more", groupOf, count: hideable.length - GROUP_SHOWN },
  ];
};

/**
 * Every organisation within `deepest` steps of `from` along `next`, other than
 * those `excluded`, and how many steps its nearest path takes.
 */
const reach = ({
  from,
  next,
  deepest,
  excluded,
}: {
  from: OrganisationId;
  next: ReadonlyMap<OrganisationId, readonly OrganisationId[]>;
  deepest: number;
  excluded: ReadonlySet<OrganisationId>;
}): Map<OrganisationId, number> => {
  const steps = new Map<OrganisationId, number>();
  let frontier = [from];
  for (let step = 1; frontier.length > 0 && step <= deepest; step += 1) {
    frontier = [
      ...new Set(frontier.flatMap((id) => next.get(id) ?? [])),
    ].filter((id) => id !== from && !excluded.has(id) && !steps.has(id));
    for (const id of frontier) {
      steps.set(id, step);
    }
  }
  return steps;
};

/**
 * Which row out from `from` each of `ids` sits in, and the organisation it is
 * drawn hanging from: one row past the farthest of the organisations nearer
 * `from` that it is a step from along `nearer`, the first in `order` of those
 * in that row. So every membership between two of them runs outwards, and none
 * lies along a row where it would cross the cards between its ends.
 */
const rowsOut = ({
  from,
  ids,
  nearer,
  order,
}: {
  from: OrganisationId;
  ids: ReadonlySet<OrganisationId>;
  nearer: ReadonlyMap<OrganisationId, readonly OrganisationId[]>;
  order: (a: OrganisationId, b: OrganisationId) => number;
}): ((id: OrganisationId) => { row: number; through: OrganisationId }) => {
  const places = new Map<
    OrganisationId,
    { row: number; through: OrganisationId }
  >([[from, { row: 0, through: from }]]);
  const counting = new Set<OrganisationId>();
  const placeOf = (
    id: OrganisationId,
  ): { row: number; through: OrganisationId } => {
    const known = places.get(id);
    if (known !== undefined) {
      return known;
    }
    if (counting.has(id)) {
      throw new Error(`The member_of structure loops back through ${id}`);
    }
    counting.add(id);
    const place = (nearer.get(id) ?? [])
      .filter((other) => other === from || ids.has(other))
      .sort(order)
      .reduce(
        (farthest, other) => {
          const row = placeOf(other).row + 1;
          return row > farthest.row ? { row, through: other } : farthest;
        },
        { row: 1, through: from },
      );
    counting.delete(id);
    places.set(id, place);
    return place;
  };
  return placeOf;
};

/**
 * Where each card and line of a structure goes: what the focus is a member of
 * in rows above it, what it operates and is operated by beside it, and its
 * members in rows below, each under one of its parents in the row above it.
 * Pure and deterministic, so the drawing is the same on every render
 * and a different layout can replace this without touching the drawing.
 */
export const layOutStructure = (
  structure: OrganisationStructure,
  options: { allLevels: boolean; expanded: ReadonlySet<string> },
): StructureLayout => {
  const focusId = structure.organisationId;
  const organisationOf = new Map<OrganisationId, StructureOrganisation>(
    structure.organisations.map((organisation) => [
      organisation.id,
      organisation,
    ]),
  );
  const sorted = byName(organisationOf);
  const known = (id: OrganisationId) => organisationOf.has(id);

  const parentsOf = new Map<OrganisationId, OrganisationId[]>();
  const membersOf = new Map<OrganisationId, OrganisationId[]>();
  const operatorsOf = new Map<OrganisationId, OrganisationId[]>();
  const operatedBy = new Map<OrganisationId, OrganisationId[]>();
  const push = (
    map: Map<OrganisationId, OrganisationId[]>,
    key: OrganisationId,
    value: OrganisationId,
  ) => {
    map.set(key, [...(map.get(key) ?? []), value]);
  };

  for (const relationship of structure.relationships) {
    const { organisationId, relatedOrganisationId } = relationship;
    if (!known(organisationId) || !known(relatedOrganisationId)) {
      continue;
    }
    match(relationship.kind)
      .with("member_of", () => {
        push(parentsOf, organisationId, relatedOrganisationId);
        push(membersOf, relatedOrganisationId, organisationId);
      })
      .with("operated_by", () => {
        push(operatorsOf, organisationId, relatedOrganisationId);
        push(operatedBy, relatedOrganisationId, organisationId);
      })
      .exhaustive();
  }

  const deepest = options.allLevels ? Number.POSITIVE_INFINITY : 1;
  const placed = new Set<OrganisationId>([focusId]);

  // Above: what the focus is a member of, in rows by `rowsOut`.
  const aboveSteps = reach({
    from: focusId,
    next: parentsOf,
    deepest,
    excluded: placed,
  });
  const above = new Set(aboveSteps.keys());
  above.forEach((id) => placed.add(id));
  const aboveOf = rowsOut({
    from: focusId,
    ids: above,
    nearer: membersOf,
    order: sorted,
  });
  const rowsAbove: OrganisationId[][] = [];
  for (const id of [...above].sort(sorted)) {
    (rowsAbove[aboveOf(id).row - 1] ??= []).push(id);
  }

  const operators = (operatorsOf.get(focusId) ?? [])
    .filter((id) => !placed.has(id))
    .sort(sorted);
  operators.forEach((id) => placed.add(id));
  const operated = grouped({
    ids: (operatedBy.get(focusId) ?? [])
      .filter((id) => !placed.has(id))
      .sort(sorted),
    groupOf: operatedGroup,
    inFull: options.expanded.has(operatedGroup),
    alwaysShown: new Set(),
  });
  operated.forEach((slot) => {
    if (slot.kind === "organisation") {
      placed.add(slot.id);
    }
  });

  // Below: the focus's members, in rows by `rowsOut`. Each hangs from a parent
  // in the row above it, making a tree so each subtree can be centred under
  // the card it hangs from.
  const belowSteps = reach({
    from: focusId,
    next: membersOf,
    deepest,
    excluded: placed,
  });
  const below = new Set(belowSteps.keys());
  const belowOf = rowsOut({
    from: focusId,
    ids: below,
    nearer: parentsOf,
    order: sorted,
  });
  const hangsFrom = new Map<OrganisationId, OrganisationId>();
  const hanging = new Map<OrganisationId, OrganisationId[]>();
  for (const id of [...below].sort(sorted)) {
    const parent = belowOf(id).through;
    hangsFrom.set(id, parent);
    push(hanging, parent, id);
  }

  // The focus's own members are what its page is about, so neither they nor
  // anything they hang from is hidden behind a count.
  const alwaysShown = new Set<OrganisationId>();
  for (const member of membersOf.get(focusId) ?? []) {
    for (
      let id: OrganisationId | undefined = member;
      id !== undefined && id !== focusId && !alwaysShown.has(id);
      id = hangsFrom.get(id)
    ) {
      alwaysShown.add(id);
    }
  }

  const childrenOf = new Map<OrganisationId, Slot[]>();
  const hangUnder = (parent: OrganisationId) => {
    const slots = grouped({
      ids: hanging.get(parent) ?? [],
      groupOf: parent,
      inFull: parent === focusId || options.expanded.has(parent),
      alwaysShown,
    });
    childrenOf.set(parent, slots);
    for (const slot of slots) {
      if (slot.kind === "organisation") {
        placed.add(slot.id);
        hangUnder(slot.id);
      }
    }
  };
  hangUnder(focusId);

  const widthOf = new Map<OrganisationId, number>();
  const subtreeWidth = (slot: Slot): number => {
    if (slot.kind === "more") {
      return CARD_WIDTH;
    }
    const known = widthOf.get(slot.id);
    if (known !== undefined) {
      return known;
    }
    const children = childrenOf.get(slot.id) ?? [];
    const width = Math.max(
      CARD_WIDTH,
      children.reduce((sum, child) => sum + subtreeWidth(child), 0) +
        GAP_X * Math.max(0, children.length - 1),
    );
    widthOf.set(slot.id, width);
    return width;
  };

  // Positions relative to the focus card's centre at (0, 0).
  const centres: { slot: Slot; key: string; cx: number; row: number }[] = [];
  const placeBelow = (id: OrganisationId, cx: number, row: number) => {
    const children = childrenOf.get(id) ?? [];
    const total =
      children.reduce((sum, child) => sum + subtreeWidth(child), 0) +
      GAP_X * Math.max(0, children.length - 1);
    let left = cx - total / 2;
    for (const child of children) {
      const width = subtreeWidth(child);
      const childCx = left + width / 2;
      centres.push({
        slot: child,
        key: child.kind === "more" ? `more:${child.groupOf}` : child.id,
        cx: childCx,
        row: row + 1,
      });
      if (child.kind === "organisation") {
        placeBelow(child.id, childCx, row + 1);
      }
      left += width + GAP_X;
    }
  };

  centres.push({
    slot: { kind: "organisation", id: focusId },
    key: focusId,
    cx: 0,
    row: 0,
  });
  placeBelow(focusId, 0, 0);

  const placeRow = (slots: readonly Slot[], row: number, startCx: number) => {
    slots.forEach((slot, index) => {
      centres.push({
        slot,
        key: slot.kind === "more" ? `more:${slot.groupOf}` : slot.id,
        cx: startCx + index * (CARD_WIDTH + GAP_X),
        row,
      });
    });
  };
  const rowWidth = (count: number) =>
    count * CARD_WIDTH + Math.max(0, count - 1) * GAP_X;

  rowsAbove.forEach((ids, index) => {
    placeRow(
      ids.map((id) => ({ kind: "organisation", id })),
      -(index + 1),
      -rowWidth(ids.length) / 2 + CARD_WIDTH / 2,
    );
  });
  // Operators run leftwards from the focus, nearest first, as operated
  // organisations run rightwards.
  placeRow(
    [...operators].reverse().map((id) => ({ kind: "organisation", id })),
    0,
    -(CARD_WIDTH + SIDE_GAP) - (operators.length - 1) * (CARD_WIDTH + GAP_X),
  );
  placeRow(operated, 0, CARD_WIDTH + SIDE_GAP);

  const minCx = Math.min(...centres.map(({ cx }) => cx));
  const maxCx = Math.max(...centres.map(({ cx }) => cx));
  const minRow = Math.min(...centres.map(({ row }) => row));
  const maxRow = Math.max(...centres.map(({ row }) => row));
  const toX = (cx: number) => cx - minCx + MARGIN + CARD_WIDTH / 2;
  const toY = (row: number) => (row - minRow) * ROW_HEIGHT + MARGIN;

  // A card's standing counts its nearest path to the focus, which can be
  // fewer steps than its row is from the focus's row.
  const standingOf = (id: OrganisationId): Standing => {
    if (id === focusId) {
      return { kind: "focus" };
    }
    const up = aboveSteps.get(id);
    if (up !== undefined) {
      return { kind: "above", levels: up };
    }
    if (operators.includes(id)) {
      return { kind: "operated-by" };
    }
    const down = belowSteps.get(id);
    return down === undefined
      ? { kind: "operates" }
      : { kind: "below", levels: down };
  };

  const cards: PlacedCard[] = centres.flatMap(
    ({ slot, key, cx, row }): PlacedCard[] => {
      const x = toX(cx) - CARD_WIDTH / 2;
      const y = toY(row);
      if (slot.kind === "more") {
        return [
          { kind: "more", key, groupOf: slot.groupOf, count: slot.count, x, y },
        ];
      }
      const organisation = organisationOf.get(slot.id);
      return organisation === undefined
        ? []
        : [
            {
              kind: "organisation",
              key,
              organisation,
              standing: standingOf(slot.id),
              x,
              y,
            },
          ];
    },
  );

  const cardAt = new Map(cards.map((card) => [card.key, card]));
  const focusCard = cardAt.get(focusId);
  if (focusCard === undefined) {
    throw new Error(
      `The structure does not hold ${focusId}, the organisation it is drawn around`,
    );
  }
  const edges: PlacedEdge[] = [];
  const memberships: OrganisationMembership[] = [];

  for (const relationship of structure.relationships) {
    const from = cardAt.get(relationship.organisationId);
    const to = cardAt.get(relationship.relatedOrganisationId);
    if (from === undefined || to === undefined) {
      continue;
    }
    match(relationship.kind)
      .with("member_of", () => {
        memberships.push({
          key: relationship.id,
          member: from,
          parent: to,
          receiverId: relationship.relatedOrganisationId,
        });
      })
      .with("operated_by", () => {
        // Operating is drawn only where the focus is one end: elsewhere it is
        // not part of where the focus sits.
        if (from === focusCard || to === focusCard) {
          edges.push(
            operatingEdge(
              relationship,
              from === focusCard ? to : from,
              focusCard,
            ),
          );
        }
      })
      .exhaustive();
  }

  // A "+N more" card stands for members of its group, so it hangs from the
  // group's parent as they would.
  for (const card of cards) {
    if (card.kind === "more" && card.groupOf !== operatedGroup) {
      const parent = cardAt.get(card.groupOf);
      if (parent?.kind === "organisation") {
        memberships.push({
          key: card.key,
          member: card,
          parent,
          receiverId: parent.organisation.id,
        });
      }
    }
  }

  // Lanes are handed out in key order, so they do not depend on the order the
  // relationships were read in.
  let lanes = 0;
  let farthestLane = 0;
  const laneBeside = (top: number, bottom: number) => {
    const passed = cards.filter((card) => card.y > top && card.y < bottom);
    const lane =
      Math.max(0, ...passed.map((card) => card.x + CARD_WIDTH)) +
      GAP_X / 2 +
      lanes * LANE_GAP;
    lanes += 1;
    farthestLane = Math.max(farthestLane, lane);
    return lane;
  };
  for (const membership of memberships.sort((a, b) =>
    a.key.localeCompare(b.key),
  )) {
    edges.push(membershipEdge(membership, laneBeside));
  }

  const focusX = focusCard.x;
  const focusY = focusCard.y;
  const operatingSide =
    operated.length > 0
      ? focusX + CARD_WIDTH
      : operators.length > 0
        ? focusX - SIDE_GAP
        : undefined;

  return {
    cards: cards.sort((a, b) => a.y - b.y || a.x - b.x),
    focus: { x: focusX + CARD_WIDTH / 2, y: focusY + CARD_HEIGHT / 2 },
    operatingLabel:
      operatingSide === undefined
        ? undefined
        : {
            x: operatingSide + 8,
            y: focusY + 8,
            width: SIDE_GAP - 16,
          },
    // Ordered by key so the drawing does not depend on the order the
    // relationships were read in.
    edges: edges.sort((a, b) => a.key.localeCompare(b.key)),
    width: Math.max(
      maxCx - minCx + CARD_WIDTH + MARGIN * 2,
      farthestLane + MARGIN,
    ),
    height: (maxRow - minRow) * ROW_HEIGHT + CARD_HEIGHT + MARGIN * 2,
  };
};

/**
 * Between the facing sides of the focus (`near`) and an organisation it
 * operates or is operated by. A farther one in the row is reached over the top
 * of the nearer ones, so its line crosses none of them.
 */
const operatingEdge = (
  relationship: { id: string; relatedOrganisationId: OrganisationId },
  other: PlacedCard,
  near: PlacedCard,
): PlacedEdge => {
  const rightwards = other.x > near.x;
  const y = near.y + CARD_HEIGHT / 2;
  const start = rightwards ? near.x + CARD_WIDTH : near.x;
  const end = rightwards ? other.x : other.x + CARD_WIDTH;
  const nearest = Math.abs(end - start) <= SIDE_GAP;
  const bend = start + (rightwards ? SIDE_GAP / 2 : -SIDE_GAP / 2);
  const busY = near.y - BUS_RISE;
  const centre = other.x + CARD_WIDTH / 2;

  return {
    key: relationship.id,
    kind: "operated_by",
    receiverId: relationship.relatedOrganisationId,
    points: nearest
      ? [
          [start, y],
          [end, y],
        ]
      : [
          [start, y],
          [bend, y],
          [bend, busY],
          [centre, busY],
          [centre, other.y],
        ],
  };
};

type OrganisationMembership = {
  key: string;
  member: PlacedCard;
  parent: PlacedCard;
  receiverId: OrganisationId;
};

/**
 * From the member's side facing the parent into the gap between rows on that
 * side, across, and into the parent's facing side, which is its bottom edge
 * for a parent in a row above. Between rows that are not neighbours it runs
 * along a lane clear of every card in the rows it passes, from `laneBeside`.
 */
const membershipEdge = (
  { key, member, parent, receiverId }: OrganisationMembership,
  laneBeside: (top: number, bottom: number) => number,
): PlacedEdge => {
  const fromX = member.x + CARD_WIDTH / 2;
  const toX = parent.x + CARD_WIDTH / 2;
  const upwards = parent.y <= member.y;
  const fromY = upwards ? member.y : member.y + CARD_HEIGHT;
  const memberGap = upwards
    ? member.y - ROW_GAP / 2
    : member.y + CARD_HEIGHT + ROW_GAP / 2;
  const intoBottom = parent.y < member.y;
  const toY = intoBottom ? parent.y + CARD_HEIGHT : parent.y;
  const parentGap = intoBottom
    ? parent.y + CARD_HEIGHT + ROW_GAP / 2
    : parent.y - ROW_GAP / 2;

  if (memberGap === parentGap) {
    return {
      key,
      kind: "member_of",
      receiverId,
      points: [
        [fromX, fromY],
        [fromX, memberGap],
        [toX, memberGap],
        [toX, toY],
      ],
    };
  }

  const lane = laneBeside(
    Math.min(memberGap, parentGap),
    Math.max(memberGap, parentGap),
  );
  return {
    key,
    kind: "member_of",
    receiverId,
    points: [
      [fromX, fromY],
      [fromX, memberGap],
      [lane, memberGap],
      [lane, parentGap],
      [toX, parentGap],
      [toX, toY],
    ],
  };
};
